import { computeFileHash } from '@/src/lib/file'
import { finalizeAnalysisUploads, prepareAnalysisUploads } from '@/src/api/ai'

type AnalysisUploadOptions = {
  files: File[]
  promptId: string
  aiModelId: string
  storeData: boolean
  fileHandling: 'single' | 'multiple'
}

type PreparedUpload = {
  fileId: string
  fileName: string
  hash: string
  contentType: string
  rawKey: string
  requiresUpload: boolean
  uploadUrl?: string
}

async function uploadFileToPresignedUrl(file: File, prepared: PreparedUpload): Promise<void> {
  if (!prepared.uploadUrl) {
    throw new Error(`Missing upload URL for ${prepared.fileName}.`)
  }

  const response = await fetch(prepared.uploadUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': file.type || prepared.contentType || 'application/octet-stream',
    },
    body: file,
  })

  if (!response.ok) {
    throw new Error(`Failed to upload ${prepared.fileName}: ${response.status} ${response.statusText}`)
  }
}

export async function uploadFilesForAnalysis(options: AnalysisUploadOptions) {
  const filesWithHash = await Promise.all(
    options.files.map(async (file) => ({
      file,
      hash: await computeFileHash(file),
    }))
  )

  const prepared = await prepareAnalysisUploads({
    data: {
      files: filesWithHash.map(({ file, hash }) => ({
        name: file.name,
        type: file.type,
        hash,
      })),
    },
  }) as PreparedUpload[]

  const filesByHash = new Map(filesWithHash.map(({ hash, file }) => [hash, file]))

  for (const item of prepared) {
    if (!item.requiresUpload) continue

    const file = filesByHash.get(item.hash)
    if (!file) {
      throw new Error(`Selected file for hash ${item.hash} is missing.`)
    }

    await uploadFileToPresignedUrl(file, item)
  }

  const result = await finalizeAnalysisUploads({
    data: {
      files: prepared.map((item) => ({
        fileId: item.fileId,
        hash: item.hash,
      })),
      promptId: options.promptId,
      aiModelId: options.aiModelId,
      storeData: options.storeData,
      fileHandling: options.fileHandling,
    },
  })

  return {
    result,
    filesWithHash,
  }
}